<template>
	<div v-if="lifeStageData" class="life-stage-info">
		<div ref="modalContentWrapper" class="modal-content-wrapper">
			<div v-if="isButtonVisible" ref="tooltipButton" class="tooltip-wrapper">
				<button
					class="tooltip-button"
					:aria-label="$t('moreInfoSavings')"
					@click="toggleTooltip"
					@mousedown.prevent
				>
					<svgicon name="savings-info" :original="true"> </svgicon>
				</button>
				<ja-tooltip v-show="showTooltip" role="status" class="left">{{
					$t(careerInfo ? careerInfo : 'noJobDescription')
				}}</ja-tooltip>
			</div>
			<ja-icon-list-element
				v-for="(item, index) in stageInfo"
				:key="index"
				:title="`${item.title}:`"
				:info="item.info"
				:show-description="item.title === 'Job'"
				class="stageInfoRow"
				role="listitem"
				><svgicon
					:name="
						`${LIFE_SCENARIO_ICONS_PATH}${item.icon}${
							item.icon === 'gross_income' && JA_LOCALIZATION_IRL ? '-ir' : ''
						}`
					"
					:style="
						`${
							item.icon === 'gross_income' && JA_LOCALIZATION_IRL
								? 'width:32px; height:32px'
								: ''
						}`
					"
				></svgicon
			></ja-icon-list-element>
		</div>
		<div class="button-wrapper">
			<slot></slot>
			<ja-button
				class="small"
				name="nextLifeScenario"
				:aria-label="$t('goNMISection')"
				@click.native="goNextStep"
			>
				{{ $t('next') }}
				<svgicon name="arrow-right"></svgicon>
			</ja-button>
		</div>
	</div>
</template>

<script>
import gql from 'graphql-tag';
import '../icons/lifeScenario';
import Button from '../ui/Button';
import IconListElement from '../ui/IconListElement';
import LifeStageData from '../../mixins/LifeStageData';
import ProfileData from '../../mixins/ProfileData';
import Tooltip from '../ui/Tooltip';
import { maritalStatus } from '@/helpers/constants';

const LIFE_SCENARIO_ICONS_PATH = 'lifeScenario/';

export default {
	components: {
		jaIconListElement: IconListElement,
		jaButton: Button,
		jaTooltip: Tooltip
	},
	mixins: [LifeStageData, ProfileData],
	data() {
		return {
			LIFE_SCENARIO_ICONS_PATH: LIFE_SCENARIO_ICONS_PATH,
			cmsCareerClusters: [],
			showTooltip: false,
			careerInfo: null,
			maritalStatus: this.$t('married'),
			JA_LOCALIZATION_IRL: this.$store.getters.isIrl,
			isButtonVisible: true
		};
	},
	apollo: {
		cmsCareerClusters: {
			query: gql`
				query clusters($jtWhere: String!) {
					cmsCareerClusters(
						where: "CmsCareers.Count(it.isActive=\\"true\\")>0"
					) {
						cmsCareers(
							where: "isActive=\\"true\\" && CmsCareerJobtitles.Count(it.isActive=\\"true\\")==4"
						) {
							career_ID
							name
							description
							cmsCareerJobTitles(
								where: $jtWhere
								order: "CmsLifeStage.DisplayOrder"
							) {
								jobTitle
								careerJobTitle_ID
							}
						}
					}
				}
			`,
			variables() {
				return {
					jtWhere: `isActive="true"`
				};
			},
			update(data) {
				return data.cmsCareerClusters;
			}
		}
	},
	computed: {
		stageSetup() {
			return this.$store.getters.stageSetupData;
		},
		stageInfo() {
			if (this.stageSetup) {
				const stageInfo = [
					{
						title: this.$t('job'),
						info: this.profileData.cmsCareerJobTitle
							? this.profileData.cmsCareerJobTitle.jobTitle
							: '',
						icon: 'job'
					},
					{
						title: this.$t('GMI'),
						info: this.$options.filters.currency(
							this.stageSetup.grossMonthlyIncome
						),
						icon: 'gross_income'
					},
					{
						title: this.$t('creditScore'),
						info: this.stageSetup.currentCreditScoreMessage,
						icon: 'credit_score'
					},
					{
						title: this.$t('relationship'),
						info: this.getRelationshipStatus(),
						icon: 'relationship'
					},
					{
						title: this.$t('lifestyleGoals'),
						info: this.selectedGoalNames,
						icon: 'goals'
					},
					{
						title: this.$t('interests'),
						info: this.stageSetup.studentInterests
							.map(x => x.cmsInterest.name)
							.join(', '),
						icon: 'interests'
					},
					{
						title: this.$t('studentSalary'),
						info: this.$options.filters.currency(
							this.profileData.netMonthlyIncome
						),
						icon: 'gross_income'
					}
				];
				if (
					this.profileData.cmsMaritalStatus.status.toLowerCase() ===
						this.maritalStatus ||
					this.profileData.cmsMaritalStatus.status.toLowerCase() ===
						maritalStatus.MARRIED.toLowerCase()
				) {
					stageInfo.push({
						title: this.$t('studentSpouseNMI'),
						info: this.$options.filters.currency(
							this.profileData.spouseNetMonthlyIncome
						),
						icon: 'gross_income'
					});
				}
				const filteredStageInfo = this.$store.getters.isIrl
					? stageInfo.filter(item => item.title !== this.$t('creditScore'))
					: this.stageSetup && stageInfo;

				return filteredStageInfo;
			}
			return null;
		},
		selectedGoalNames() {
			const result = this.lifeStageData.studentGoals
				.map(x => {
					if (x.cmsGoal) {
						return x.cmsGoal.name;
					}
				})
				.filter(x => x)
				.join(', ');
			return result;
		}
	},
	mounted() {
		const modalContentWrapper = this.$refs.modalContentWrapper;

		modalContentWrapper.addEventListener('scroll', this.handleScroll);
	},
	methods: {
		handleScroll() {
			const modalContentWrapper = this.$refs.modalContentWrapper;
			if (modalContentWrapper.scrollTop > 5) {
				this.isButtonVisible = false;
			} else {
				this.isButtonVisible = true;
			}
		},
		goNextStep() {
			this.findCareer();
			this.$emit('goNextStep');
		},
		getRelationshipStatus() {
			const relationship = this.profileData.cmsMaritalStatus.status;

			const childrenLiteral =
				this.childrenCount > 1 ? this.$t('children') : this.$t('child');

			return `${relationship} ${this.$t('with')} ${
				this.childrenCount
			} ${childrenLiteral}`;
		},
		findCareer() {
			const jobTitle = this.profileData.cmsCareerJobTitle.jobTitle;
			const foundCareer = this.cmsCareerClusters
				.map(obj =>
					obj.cmsCareers.find(career =>
						career.cmsCareerJobTitles.some(job => job.jobTitle === jobTitle)
					)
				)
				.find(career => !!career);
			if (foundCareer) {
				this.careerInfo = foundCareer.description;
			}
		},
		toggleTooltip() {
			this.showTooltip = !this.showTooltip;
			if (!this.careerInfo) {
				this.findCareer();
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.life-stage-info {
	width: 100%;

	.modal-content-wrapper {
		width: 66%;
		height: 30vh;
		margin: auto;
		margin-top: 24px;
		margin-bottom: 24px;
		display: inline-table;

		@media screen and (min-width: 1280px) and (max-width: 1440px) {
			max-height: 40vh;
			overflow-y: auto;
		}

		@media screen and (min-width: 1440px) {
			height: 45vh;
			overflow-y: auto;
		}
	}
	.tooltip-wrapper {
		left: 280px;
		top: 208px;
		width: 45%;
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		position: absolute;
		align-items: flex-start;
		gap: 10px;

		.tooltip-button {
			background-color: transparent;
			border: 0;
			padding: 0;

			::v-deep path {
				fill: $info-icon-color;
			}
		}

		.tooltip-msg {
			transform: translate(36px, -50%);
			font-size: 24px;
			width: 320px;
			top: 0;
			left: -20px;
			position: relative;
		}

		svg {
			height: 24px;
			width: 24px;
			&:hover {
				cursor: pointer;
			}
		}
	}
}
</style>
